import { memo, useCallback, useEffect, useRef } from 'react'

import { ContentManagedStoreEdit } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useManagedStoreUpdateMutation } from 'hooks/mutations/content'
import { managedStoreConfigRoute } from 'pages/NWPlatform/ContentStores/ManagedStores/ManagedStore/Routing'
import { managedStoreQueryKeys } from 'services/queryKeys'

const ConfigRaw = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('domain')

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { managedStoreId } = managedStoreConfigRoute.useParams()

  const { data, isLoading } = useQuery(managedStoreQueryKeys.detail({ storeId: managedStoreId }))

  const { mutate: updateManagedStore } = useManagedStoreUpdateMutation()

  const handleSave = useCallback(
    (value: ContentManagedStoreEdit) => {
      updateManagedStore(
        { storeId: managedStoreId, contentManagedStoreEdit: value },
        {
          onSuccess: () => {
            enqueueSnackbar(t('ContentStores.managedStoreSaved'), {
              variant: 'success',
            })
          },
          onError: () => {
            enqueueSnackbar(t('ContentStores.errorCreateRetry'), {
              variant: 'error',
            })
          },
        },
      )
    },
    [enqueueSnackbar, managedStoreId, updateManagedStore, t],
  )

  useEffect(() => {
    if (editorInstanceRef.current && !!data) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(data, null, 4))
    }
  }, [data])

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (!data) {
    return <NotFound />
  }

  return (
    <Editor
      value={data}
      onSubmit={handleSave}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const Config = memo(ConfigRaw)
